import { Footprints, PlayCircle } from "lucide-react"
import { useMemo } from "react"
import {
	beginnerSteps,
	MountainIllustration,
	useBeginnerProgress,
	useToggleBeginnerStep,
} from "@/entities/beginner-path"
import { useResolvedPostsByUrls } from "@/entities/post"
import { pluralize } from "@/shared/lib/pluralize"
import { Accordion } from "@/shared/ui/accordion"
import { PageTransition } from "@/shared/ui/page-transition"
import { SectionHeading } from "@/shared/ui/section-heading"
import { StepItem } from "./step-item"

function Stat({ icon: Icon, label }: { icon: typeof Footprints; label: string }) {
	return (
		<span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium whitespace-nowrap">
			<Icon className="size-3.5 text-primary" />
			{label}
		</span>
	)
}

export function BeginnerPathPage() {
	const urls = useMemo(() => {
		return Array.from(new Set(beginnerSteps.flatMap((step) => step.links.map((l) => l.url))))
	}, [])

	const { data: resolved = [] } = useResolvedPostsByUrls(urls)
	const resolvedByUrl = useMemo(() => {
		return new Map(resolved.map((post) => [post.postUrl, post.id] as const))
	}, [resolved])

	const { data: completedSteps = [] } = useBeginnerProgress()
	const { mutate: toggleStep } = useToggleBeginnerStep()
	const completed = useMemo(() => new Set(completedSteps), [completedSteps])

	const total = beginnerSteps.length
	const done = completed.size
	const percent = Math.round((done / total) * 100)

	return (
		<PageTransition>
			<header className="relative isolate -mt-[var(--app-body-padding-top)] overflow-hidden pt-[var(--app-body-padding-top)]">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[26rem] bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_28%,transparent),transparent_70%)]"
				/>
				<MountainIllustration className="absolute right-0 bottom-0 h-36 w-[42%]" />

				<div className="container mx-auto max-w-3xl px-3 pt-6 pb-8 sm:px-4">
					<div className="relative max-w-[64%] sm:max-w-md">
						<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
							Старт здесь
						</p>
						<h1 className="mt-1.5 text-3xl font-bold tracking-tight">Путь новичка</h1>
						<p className="mt-2 text-sm leading-snug text-muted-foreground">
							Смотрите урок, внедряете, получаете первые результаты.
						</p>
						<div className="mt-4 flex gap-2">
							<Stat icon={Footprints} label={pluralize(total, ["шаг", "шага", "шагов"])} />
							<Stat
								icon={PlayCircle}
								label={pluralize(urls.length, ["материал", "материала", "материалов"])}
							/>
						</div>
					</div>
				</div>
			</header>

			<div className="container mx-auto max-w-3xl px-3 pb-6 sm:px-4 lg:pb-8">
				<SectionHeading
					title="Шаги"
					className="mb-2"
					trailing={
						<span className="shrink-0 text-sm text-muted-foreground">
							Пройдено {done} из {total}
						</span>
					}
				/>
				<div
					role="progressbar"
					aria-valuemin={0}
					aria-valuemax={total}
					aria-valuenow={done}
					aria-label="Прогресс по пути новичка"
					className="mb-5 h-1 overflow-hidden rounded-full bg-white/10"
				>
					<div
						className="h-full rounded-full bg-primary transition-[width] duration-300 ease-[var(--ease-out-soft)]"
						style={{ width: `${percent}%` }}
					/>
				</div>

				<div className="relative">
					<span
						aria-hidden
						className="pointer-events-none absolute top-8 bottom-8 left-[15px] w-px bg-white/10"
					/>
					<Accordion type="multiple" defaultValue={["step-1"]} className="space-y-3">
						{beginnerSteps.map((step) => (
							<StepItem
								key={step.step}
								{...step}
								completed={completed.has(step.step)}
								onToggle={() =>
									toggleStep({ step: step.step, completed: !completed.has(step.step) })
								}
								resolvePostId={(url) => resolvedByUrl.get(url)}
							/>
						))}
					</Accordion>
				</div>
			</div>
		</PageTransition>
	)
}
